import { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useNewFeedbackStream } from '../../hooks/useNewFeedbackStream';
import { formatDateString, isCalendarToday } from '../../utils/dateFormat';

/**
 * Right-rail feed for the dashboard's selected report date (not global "latest 8").
 */

const Stars = ({ n }) => {
  const filled = Math.max(0, Math.min(5, n || 0));
  return (
    <div className="stars" aria-label={`${filled} of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <i key={i} className={i < filled ? 'fill' : ''} />
      ))}
    </div>
  );
};

const AREA_NAMES = {
  1: 'CHARMINAR',
  2: 'KARWAN',
  3: 'SANTOSH NAGAR',
  4: 'GOSHAMAHAL',
  5: 'SECUNDERABAD',
  6: 'POLICE STATIONS HYDERABAD',
  7: 'POLICE STATIONS CYBERABAD',
};

export default function LiveFeedbackRail({
  size = 8,
  totalToday = null,
  variant = 'rail',
  selectedDate = new Date(),
}) {
  const dateString = formatDateString(selectedDate);
  const watchingToday = isCalendarToday(selectedDate);
  const { items, latest } = useNewFeedbackStream({
    size,
    dateString,
    pulseOnArrival: watchingToday,
  });
  const [bloomedId, setBloomedId] = useState(null);
  const bloomTimerRef = useRef(null);

  const dateLabel = useMemo(
    () => selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase(),
    [selectedDate],
  );

  useEffect(() => {
    if (!latest) return;
    setBloomedId(latest.id);
    clearTimeout(bloomTimerRef.current);
    bloomTimerRef.current = setTimeout(() => setBloomedId(null), 1200);
    return () => clearTimeout(bloomTimerRef.current);
  }, [latest]);

  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  const newCount = watchingToday
    ? items.filter((it) => it.when && it.when.getTime() >= oneHourAgo).length
    : 0;

  const railTitle = watchingToday ? 'LIVE' : 'FEEDBACK';
  const railCount = watchingToday
    ? `${newCount} NEW · 1H`
    : `${items.length} · ${dateLabel}`;

  const emptyMessage = totalToday === 0 || items.length === 0
    ? `No feedback for ${dateLabel}`
    : 'Waiting for feedback…';

  if (variant === 'phone') {
    return (
      <section className="db-live-phone" aria-label={`Feedback for ${dateLabel}`}>
        <div className="head">
          <div className="live"><span className="db-live-dot" />{railTitle}</div>
          <div className="count">{railCount}</div>
        </div>

        <div className="feed">
          {items.length === 0 && (
            <div className="card" style={{ color: 'var(--c-ink-3)' }}>
              <div className="sup" style={{ fontWeight: 500 }}>{emptyMessage}</div>
            </div>
          )}
          {items.map((it, idx) => {
            const isNew = idx === 0 && bloomedId === it.id;
            const area = it.area || '';
            const cls = ['card', isNew ? 'brand-new' : ''].filter(Boolean).join(' ');
            return (
              <div key={it.id} className={cls}>
                <Stars n={it.stars} />
                <div className="time">{(it.relative || '').toUpperCase()}</div>
                <div className="sup">{it.supervisorName || '—'}</div>
                <div className="loc">
                  <em>{it.shopName || '—'}</em>{area ? ` · ${area}` : ''}
                </div>
              </div>
            );
          })}
        </div>

        <div className="foot">
          <span>SHOWING {items.length} / {totalToday ?? '—'}</span>
          <Link to="/dashboard/feedback">ALL FEEDBACK →</Link>
        </div>
      </section>
    );
  }

  return (
    <aside className="db-rail" aria-label={`Feedback for ${dateLabel}`}>
      <div className="db-rail-head">
        <div className="db-live"><span className="db-live-dot" />{railTitle}</div>
        <div className="db-rail-count">{railCount}</div>
      </div>

      <div className="db-feed">
        {items.length === 0 && (
          <div className="db-card" style={{ color: 'var(--c-ink-3)' }}>
            <div className="sup" style={{ fontWeight: 500 }}>{emptyMessage}</div>
          </div>
        )}
        {items.map((it, idx) => {
          const isLow = it.stars > 0 && it.stars <= 3;
          const isVeryLow = it.stars > 0 && it.stars <= 2;
          const isNew = idx === 0 && bloomedId === it.id;
          const area = it.area || '';
          const cls = [
            'db-card',
            isNew ? 'brand-new' : '',
            isLow ? 'low' : '',
            isVeryLow ? 'low-2' : '',
          ].filter(Boolean).join(' ');
          return (
            <div key={it.id} className={cls}>
              <Stars n={it.stars} />
              <div className="time">{(it.relative || '').toUpperCase()}</div>
              <div className="sup">{it.supervisorName || '—'}</div>
              <div className="loc">
                <em>{it.shopName || '—'}</em>{area ? ` · ${area}` : ''}
              </div>
            </div>
          );
        })}
      </div>

      <div className="db-rail-foot">
        <span>SHOWING {items.length} / {totalToday ?? '—'}</span>
        <Link to="/dashboard/feedback">ALL FEEDBACK →</Link>
      </div>
    </aside>
  );
}

export { AREA_NAMES };
